import { useEffect, useRef, useState } from 'react';
import {
  addGuestMinute,
  createXpTabLeader,
  fetchAccountXp,
  getGuestXp,
  getLastKnownHallXp,
  getLastKnownXp,
  publishXp,
  TICK_MS,
  XP_EVENT
} from './xp';

/**
 * Latest XP for display. Survives hall remounts via a shared in-memory value
 * and stays current by listening for XP broadcasts + fetching from the server.
 */
export function useCurrentXp({ isAuthenticated, seedXp, hallSlug } = {}) {
  const seedRef = useRef(seedXp);
  seedRef.current = seedXp;
  const [xp, setXp] = useState(() => {
    if (typeof getLastKnownXp() === 'number') return getLastKnownXp();
    if (isAuthenticated && typeof seedXp === 'number') return seedXp;
    if (!isAuthenticated) return getGuestXp();
    return 0;
  });
  const [hallXp, setHallXp] = useState(() => (
    typeof getLastKnownHallXp() === 'number' ? getLastKnownHallXp() : 0
  ));

  useEffect(() => {
    const onXp = (e) => {
      if (typeof e.detail?.xp === 'number') setXp(e.detail.xp);
      if (typeof e.detail?.hallXp === 'number') setHallXp(e.detail.hallXp);
    };
    window.addEventListener(XP_EVENT, onXp);

    if (isAuthenticated) {
      fetchAccountXp(hallSlug).then((serverXp) => {
        if (typeof serverXp === 'number') return;
        if (typeof getLastKnownXp() === 'number') {
          setXp(getLastKnownXp());
          return;
        }
        if (typeof seedRef.current === 'number') publishXp(seedRef.current);
      });
    } else if (typeof getLastKnownXp() === 'number') {
      setXp(getLastKnownXp());
    } else {
      setXp(getGuestXp());
    }

    return () => window.removeEventListener(XP_EVENT, onXp);
  }, [isAuthenticated, hallSlug]);

  return { xp, hallXp };
}

/**
 * Awards 1 XP per minute while this LiveWorld is mounted, the tab is visible,
 * and this tab holds the XP leadership lock.
 */
export function useXpTicker({ enabled, isAuthenticated, hallSlug }) {
  useEffect(() => {
    if (!enabled) return undefined;

    let leader = false;
    const stopLeader = createXpTabLeader((isLeader) => {
      leader = isLeader;
    });

    const tick = async () => {
      if (!leader) return;
      if (document.visibilityState !== 'visible') return;

      if (isAuthenticated) {
        try {
          const res = await fetch('/api/xp/tick', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ hallSlug: hallSlug || 'commons' })
          });
          if (!res.ok) return;
          const data = await res.json();
          if (typeof data.xp === 'number') publishXp(data.xp, data.hallXp);
        } catch {
          // Ignore transient network errors; the next minute retries.
        }
        return;
      }

      addGuestMinute();
    };

    const timer = setInterval(tick, TICK_MS);
    return () => {
      stopLeader();
      clearInterval(timer);
    };
  }, [enabled, isAuthenticated, hallSlug]);
}
